import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Users, Check, X, UserPlus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../utils/api';

const SubscriptionAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrant, setShowGrant] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, plansRes] = await Promise.all([
        subscriptionAPI.adminAll(),
        subscriptionAPI.adminPlans(),
      ]);
      setSubscriptions(subsRes.data);
      setPlans(plansRes.data);
    } catch {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadUsers = async () => {
    try {
      const { data } = await subscriptionAPI.adminUsers();
      setUsers(data);
    } catch {
      toast.error('Failed to load users');
    }
  };

  const handleStatusChange = async (subId, newStatus) => {
    try {
      await subscriptionAPI.adminUpdate(subId, newStatus);
      toast.success(`Subscription ${newStatus.toLowerCase()}`);
      loadData();
    } catch {
      toast.error('Failed to update subscription');
    }
  };

  const handleDelete = async (sub) => {
    const label = `${sub.user_name || sub.user_email || 'this user'}'s ${sub.plan_name || sub.plan_type}`;
    if (!window.confirm(`Permanently delete ${label}? This removes the subscription record entirely (use Cancel if you just want to disable it).`)) return;
    try {
      await subscriptionAPI.adminDelete(sub.id);
      toast.success('Subscription deleted');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete subscription');
    }
  };

  if (!user || !['MASTER', 'LMASTER'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#9E9E9E]">Admin access required</p>
      </div>
    );
  }

  const activeSubs = subscriptions.filter(s => s.status === 'ACTIVE');
  const riderSubs = subscriptions.filter(s => s.plan_type === 'RIDER');
  const producerSubs = subscriptions.filter(s => s.plan_type === 'PRODUCER');

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors text-sm" data-testid="sub-back-btn">
              <ArrowLeft size={18} /> Back to Dashboard
            </button>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Subscription Management</h1>
            <div />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Subscriptions" value={subscriptions.length} color="#0096FF" />
          <StatCard label="Active" value={activeSubs.length} color="#34C759" />
          <StatCard label="Rider Subs" value={riderSubs.length} color="#AF52DE" />
          <StatCard label="Producer Subs" value={producerSubs.length} color="#FF9500" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#161616] border border-[#262626] rounded-lg p-1 w-fit">
          {[
            { id: 'subscriptions', label: 'All Subscriptions', icon: CreditCard },
            { id: 'plans', label: 'Plans', icon: PencilSimple },
            { id: 'grant', label: 'Grant Access', icon: UserPlus },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); if (t.id === 'grant') loadUsers(); }}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                tab === t.id ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="shimmer h-10 w-10 rounded-full"></div></div>
        ) : tab === 'subscriptions' ? (
          <SubscriptionsTable subscriptions={subscriptions} onStatusChange={handleStatusChange} onDelete={handleDelete} />
        ) : tab === 'plans' ? (
          <PlansManager plans={plans} onUpdate={loadData} editPlan={editPlan} setEditPlan={setEditPlan} />
        ) : (
          <GrantAccess users={users} plans={plans} subscriptions={subscriptions} onGrant={loadData} />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-[#161616] border border-[#262626] rounded-lg p-4">
    <p className="text-xs text-[#9E9E9E] mb-1">{label}</p>
    <p className="text-2xl font-black" style={{ color, fontFamily: 'Outfit' }}>{value}</p>
  </div>
);

const SubscriptionsTable = ({ subscriptions, onStatusChange, onDelete }) => (
  <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid="subscriptions-table">
    <div className="px-5 py-4 border-b border-[#262626]">
      <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>All Subscriptions</h3>
    </div>
    {subscriptions.length === 0 ? (
      <div className="p-8 text-center text-[#9E9E9E] text-sm">No subscriptions yet</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[#9E9E9E] text-xs border-b border-[#262626]">
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Price</th>
              <th className="px-4 py-3 text-left">Started</th>
              <th className="px-4 py-3 text-left">Expires</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map(sub => (
              <tr key={sub.id} className="border-b border-[#262626]/50" data-testid={`sub-row-${sub.id}`}>
                <td className="px-4 py-3">
                  <p className="text-white font-semibold">{sub.user_name || 'Unknown'}</p>
                  <p className="text-[#9E9E9E] text-xs">{sub.user_email}</p>
                </td>
                <td className="px-4 py-3 text-white">{sub.plan_name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    sub.plan_type === 'RIDER' ? 'bg-[#AF52DE]/20 text-[#AF52DE]' : 'bg-[#FF9500]/20 text-[#FF9500]'
                  }`}>{sub.plan_type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    sub.status === 'ACTIVE' ? 'bg-[#34C759]/20 text-[#34C759]' :
                    sub.status === 'CANCELLED' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#9E9E9E]/20 text-[#9E9E9E]'
                  }`}>{sub.status}</span>
                </td>
                <td className="px-4 py-3 text-white font-mono">${sub.price?.toFixed(2) || '0.00'}</td>
                <td className="px-4 py-3 text-[#9E9E9E] text-xs">{sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-[#9E9E9E] text-xs">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {sub.status !== 'ACTIVE' && (
                      <button onClick={() => onStatusChange(sub.id, 'ACTIVE')} className="px-2 py-1 bg-[#34C759]/10 text-[#34C759] text-xs rounded hover:bg-[#34C759]/20" data-testid={`activate-${sub.id}`}>
                        <Check size={12} weight="bold" /> Activate
                      </button>
                    )}
                    {sub.status === 'ACTIVE' && (
                      <button onClick={() => onStatusChange(sub.id, 'CANCELLED')} className="px-2 py-1 bg-[#FF3B30]/10 text-[#FF3B30] text-xs rounded hover:bg-[#FF3B30]/20" data-testid={`cancel-${sub.id}`}>
                        <X size={12} weight="bold" /> Cancel
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(sub)}
                      className="px-2 py-1 bg-[#FF3B30]/10 text-[#FF3B30] text-xs rounded hover:bg-[#FF3B30]/30 inline-flex items-center gap-1"
                      title="Permanently delete this subscription"
                      data-testid={`delete-sub-${sub.id}`}
                    >
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const PlansManager = ({ plans, onUpdate, editPlan, setEditPlan }) => {
  const [editPrice, setEditPrice] = useState('');
  const [editName, setEditName] = useState('');

  const startEdit = (plan) => {
    setEditPlan(plan.id);
    setEditPrice(plan.price.toString());
    setEditName(plan.name);
  };

  const saveEdit = async (planId) => {
    try {
      await subscriptionAPI.adminUpdatePlan(planId, { price: Number(editPrice), name: editName });
      toast.success('Plan updated');
      setEditPlan(null);
      onUpdate();
    } catch {
      toast.error('Failed to update plan');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="plans-manager">
      {plans.map(plan => (
        <div key={plan.id} className="bg-[#161616] border border-[#262626] rounded-lg p-6" data-testid={`plan-card-${plan.type}`}>
          {editPlan === plan.id ? (
            <div className="space-y-4">
              <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm" />
              <div>
                <label className="text-xs text-[#9E9E9E]">Price ($/month)</label>
                <input type="number" step="0.01" value={editPrice} onChange={e => setEditPrice(e.target.value)} className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm mt-1" data-testid={`edit-price-${plan.type}`} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => saveEdit(plan.id)} className="px-4 py-2 bg-[#34C759] text-white text-sm rounded-md font-semibold">Save</button>
                <button onClick={() => setEditPlan(null)} className="px-4 py-2 bg-[#262626] text-[#9E9E9E] text-sm rounded-md">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{plan.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                    plan.type === 'RIDER' ? 'bg-[#AF52DE]/20 text-[#AF52DE]' : 'bg-[#FF9500]/20 text-[#FF9500]'
                  }`}>{plan.type}</span>
                </div>
                <button onClick={() => startEdit(plan)} className="text-[#9E9E9E] hover:text-white p-1" data-testid={`edit-plan-${plan.type}`}>
                  <PencilSimple size={16} />
                </button>
              </div>
              <p className="text-3xl font-black text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                ${plan.price.toFixed(2)}<span className="text-sm text-[#9E9E9E] font-normal">/{plan.interval}</span>
              </p>
              <ul className="space-y-2">
                {(plan.features || []).map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9E9E9E]">
                    <Check size={14} className="text-[#34C759] flex-shrink-0" weight="bold" /> {f}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const GrantAccess = ({ users, plans, subscriptions, onGrant }) => {
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedPlan, setSelectedPlan] = useState('');
  const [search, setSearch] = useState('');
  const [granting, setGranting] = useState(false);

  const activeSubUserPlanMap = {};
  subscriptions.filter(s => s.status === 'ACTIVE').forEach(s => {
    activeSubUserPlanMap[`${s.user_id}_${s.plan_type}`] = true;
  });

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleUser = (uid) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      filteredUsers.forEach((u) => next.add(u.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedUserIds(new Set());

  const handleGrant = async () => {
    if (selectedUserIds.size === 0 || !selectedPlan) {
      toast.error('Select at least one user and a plan');
      return;
    }
    setGranting(true);
    try {
      // Single → use existing endpoint; multi → bulk endpoint.
      if (selectedUserIds.size === 1) {
        const onlyId = Array.from(selectedUserIds)[0];
        await subscriptionAPI.adminGrant(onlyId, selectedPlan);
        toast.success('Subscription granted!');
      } else {
        const { data } = await subscriptionAPI.adminGrantBulk(
          Array.from(selectedUserIds), selectedPlan
        );
        let msg = `Granted to ${data.granted_count} user${data.granted_count === 1 ? '' : 's'}`;
        if (data.skipped_count > 0) msg += ` · ${data.skipped_count} already had it`;
        if (data.failed_count > 0) msg += ` · ${data.failed_count} failed`;
        toast.success(msg);
      }
      clearSelection();
      setSelectedPlan('');
      onGrant();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant subscription');
    } finally {
      setGranting(false);
    }
  };

  return (
    <div className="bg-[#161616] border border-[#262626] rounded-lg p-6" data-testid="grant-access">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Grant Subscription</h3>
        <div className="text-xs text-[#9E9E9E]" data-testid="grant-selected-count">
          {selectedUserIds.size} selected
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Search & Select Users</label>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            data-testid="grant-search"
            className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm placeholder:text-[#555] focus:border-[#0096FF] focus:outline-none mb-2"
          />
          <div className="flex items-center gap-3 mb-2 text-[10px]">
            <button onClick={selectAllVisible} className="text-[#0096FF] hover:underline" data-testid="grant-select-all">Select All Visible</button>
            {selectedUserIds.size > 0 && (
              <button onClick={clearSelection} className="text-[#FF9500] hover:underline" data-testid="grant-clear-selection">Clear ({selectedUserIds.size})</button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredUsers.map(u => {
              const hasRider = activeSubUserPlanMap[`${u.id}_RIDER`];
              const hasProducer = activeSubUserPlanMap[`${u.id}_PRODUCER`];
              const isChecked = selectedUserIds.has(u.id);
              return (
                <label
                  key={u.id}
                  data-testid={`grant-user-${u.id}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    isChecked ? 'bg-[#0096FF]/10 border border-[#0096FF]/30' : 'hover:bg-[#262626] border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleUser(u.id)}
                      className="accent-[#0096FF] flex-shrink-0"
                      data-testid={`grant-user-checkbox-${u.id}`}
                    />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-[#9E9E9E] text-xs truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {hasRider && <span className="text-[8px] px-1 py-0.5 bg-[#AF52DE]/20 text-[#AF52DE] rounded">RIDER</span>}
                    {hasProducer && <span className="text-[8px] px-1 py-0.5 bg-[#FF9500]/20 text-[#FF9500] rounded">PRODUCER</span>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Select Plan</label>
          <div className="space-y-2 mb-6">
            {plans.map(p => (
              <div
                key={p.id}
                onClick={() => setSelectedPlan(p.id)}
                data-testid={`grant-plan-${p.type}`}
                className={`px-4 py-3 rounded-lg cursor-pointer transition-colors border ${
                  selectedPlan === p.id
                    ? 'bg-[#0096FF]/10 border-[#0096FF]'
                    : 'bg-[#0A0A0A] border-[#262626] hover:border-[#0096FF]/50'
                }`}
              >
                <p className="text-white font-semibold text-sm">{p.name}</p>
                <p className="text-[#9E9E9E] text-xs">${p.price.toFixed(2)}/{p.interval} &middot; {p.type}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleGrant}
            disabled={selectedUserIds.size === 0 || !selectedPlan || granting}
            data-testid="grant-submit"
            className="w-full px-4 py-3 bg-[#0096FF] text-white font-semibold rounded-lg hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {granting
              ? 'Granting...'
              : selectedUserIds.size > 1
                ? `Grant to ${selectedUserIds.size} Users`
                : 'Grant Subscription'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAdmin;
